import React from 'react';
import { Box } from '@mui/material';

type Props = {
  /** Accent colour of the card (header gradient, border and shadow tint). */
  accent: string;
  /** Optional second gradient stop; defaults to the Notes-style warm gold. */
  accent2?: string;
  grid: boolean;
  /** Large faint icon in the header corner. */
  watermark?: React.ComponentType<any>;
  /** Content of the gradient header (grid) / spine (list). */
  header: React.ReactNode;
  /** Content of the white body. */
  children: React.ReactNode;
  selected?: boolean;
  index?: number;
  onClick?: () => void;
};

/**
 * The Notes-page card look, shared: a glossy gradient header block (top in grid mode, left spine in list
 * mode) over a white body. Payments and Grocery use it so every list reads the same way.
 */
const NoteStyleCard = ({
  accent, accent2 = '#F6A938', grid, watermark: Watermark, header, children, selected, index = 0, onClick,
}: Props) => (
  <Box
    className="qs-animate-in"
    onClick={onClick}
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: grid ? 'column' : 'row',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      borderRadius: '24px',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: selected ? accent : `${accent}33`,
      boxShadow: selected ? `0 0 0 3px ${accent}40, 0 12px 28px ${accent}22` : `0 12px 28px ${accent}22`,
      animationDelay: `${Math.min(index, 12) * 0.04}s`,
      transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
      '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 22px 40px ${accent}44` },
    }}
  >
    <Box
      sx={{
        position: 'relative', overflow: 'hidden', color: '#fff', flexShrink: 0, p: 2,
        width: grid ? 'auto' : { xs: 96, sm: 132 },
        minHeight: grid ? 96 : 'auto',
        display: 'flex',
        flexDirection: grid ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: grid ? 'space-between' : 'center',
        gap: grid ? 1.5 : 1,
        background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 55%, ${accent2} 130%)`,
        '&::before': {
          content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(135deg, rgba(255,255,255,.4) 0%, rgba(255,255,255,.08) 45%, rgba(255,255,255,0) 46%, rgba(0,0,0,.12) 100%)',
        },
      }}
    >
      {Watermark && (
        <Watermark sx={{ position: 'absolute', right: -10, bottom: -18, fontSize: 96, color: 'rgba(255,255,255,.2)', transform: 'rotate(-12deg)' }} />
      )}
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: grid ? 'row' : 'column', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
        {header}
      </Box>
    </Box>

    <Box sx={{ flex: 1, minWidth: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {children}
    </Box>
  </Box>
);

/** The frosted glass icon tile used in card headers. */
export const GlassIconTile = ({ children, size = 46 }: { children: React.ReactNode; size?: number }) => (
  <Box
    sx={{
      width: size, height: size, flexShrink: 0, display: 'grid', placeItems: 'center',
      borderRadius: '16px', color: '#fff',
      background: 'linear-gradient(135deg, rgba(255,255,255,.42), rgba(255,255,255,.12))',
      border: '1px solid rgba(255,255,255,.5)', backdropFilter: 'blur(6px)',
    }}
  >
    {children}
  </Box>
);

export default NoteStyleCard;
